import { Controller, useFormContext } from "react-hook-form";

import {
  Field,
  FormSection,
  TextInput,
  Textarea,
} from "@/shared/foundation/primitives";
import textStyles from "@/shared/foundation/styles/textStyles.module.css";
import { agentFormSectionId } from "@/shared/navigation";

import { type AgentFormValues } from "../agentFormSchema";

import styles from "./sections.module.css";

/**
 * What the agent is called and what it is for. Last when creating, because it
 * is the one question that is easier to answer once the rest is written down;
 * first when editing, where it names the agent being changed.
 *
 * Unlike a trigger's, an agent's name is not its identifier, so it stays
 * editable after the agent exists.
 */
export function NameSection() {
  const { control } = useFormContext<AgentFormValues>();

  return (
    <FormSection
      title="Basics"
      titleId={agentFormSectionId("name")}
      hint="How teammates will find and recognize this agent."
    >
      <Controller
        control={control}
        name="name"
        render={({ field, fieldState }) => (
          <Field.Root
            name={field.name}
            invalid={fieldState.invalid}
            data-testid="agent-name-field"
          >
            <Field.Label>
              <span className={textStyles.overline}>Name</span>
            </Field.Label>
            <Field.Control
              render={
                <TextInput autoComplete="off" spellCheck={false} size="lg" />
              }
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              placeholder="Triage anomalous drives"
            />
            <Field.Error match>{fieldState.error?.message}</Field.Error>
          </Field.Root>
        )}
      />

      <Controller
        control={control}
        name="description"
        render={({ field, fieldState }) => (
          <Field.Root
            name={field.name}
            invalid={fieldState.invalid}
            className={styles.row}
            data-testid="agent-description-field"
          >
            <Field.Label>
              <span className={textStyles.overline}>Description</span>
            </Field.Label>
            <Field.Control
              render={<Textarea rows={3} />}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              placeholder="What this agent does, and when a teammate should run it."
            />
            <Field.Error match>{fieldState.error?.message}</Field.Error>
          </Field.Root>
        )}
      />
    </FormSection>
  );
}
